/**
 * Verbesserungen für Barrierefreiheit bei Jet Engine Popup
 *
 * 1. Fügt dem Schließen-Button A11y-Attribute hinzu und macht ihn per Tastatur bedienbar.
 * 2. Implementiert eine "Focus Trap", damit der Tastaturfokus im geöffneten Popup bleibt.
 * 3. Ermöglicht das Schließen des Popups mit der 'Escape'-Taste.
 * 4. Speichert und stellt den Fokus nach dem Schließen des Popups wieder her.
 */
function initPopupCloseButtonOptimization() {

    let activeFocusTrapHandler = null;
    let lastFocusedElementBeforePopup = null;

    // --- Übersetzungen ---
    const i18nGlobal = (typeof WCO_UI_Translations !== 'undefined') ? WCO_UI_Translations : {};
    const i18nPopup = i18nGlobal.popup || {};
    const closeLabel = i18nPopup.closeLabel || 'Close popup';

    /**
     * Wendet A11y-Verbesserungen auf den Schließen-Button an.
     * @param {HTMLElement} popup - Das Haupt-Popup-Element.
     */
    const optimizeCloseButton = (popup) => {
        const closeButton = popup.querySelector('.jet-popup__close-button');
        // Führe Aktionen nur aus, wenn der Button existiert und noch nicht von uns bearbeitet wurde.
        if (closeButton && !closeButton.hasAttribute('role')) {
            closeButton.setAttribute('aria-label', closeLabel);
            closeButton.setAttribute('tabindex', '0');
            closeButton.setAttribute('role', 'button');

            // NEU: Keyboard-Event-Handler hinzufügen, damit der Button auf Enter/Leertaste reagiert.
            closeButton.addEventListener('keydown', (event) => {
                // Ein Button sollte auf 'Enter' oder 'Space' reagieren.
                if (event.key === 'Enter' || event.key === ' ') {
                    // Verhindert das Standardverhalten (z.B. Scrollen bei Leertaste).
                    event.preventDefault();
                    // Löst das 'click'-Event aus, auf das JetEngine intern wartet.
                    closeButton.click();
                }
            });
        }
    };

    /**
     * Erstellt eine "Focus Trap" für das angegebene Popup-Element.
     * @param {HTMLElement} popup - Das Haupt-Popup-Element.
     */
    const setupFocusTrap = (popup) => {
        setTimeout(() => {
            const focusableElementsSelector = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), details, [tabindex]:not([tabindex="-1"])';
            const focusableElements = Array.from(popup.querySelectorAll(focusableElementsSelector))
                .filter(el => el.offsetParent !== null);

            if (focusableElements.length === 0) return;

            const firstFocusableElement = focusableElements[0];
            const lastFocusableElement = focusableElements[focusableElements.length - 1];

            firstFocusableElement.focus();

            activeFocusTrapHandler = (event) => {
                if (event.key === 'Escape') {
                    const closeButton = popup.querySelector('.jet-popup__close-button');
                    if (closeButton) {
                        closeButton.click();
                    }
                    return;
                }

                if (event.key !== 'Tab') return;

                if (event.shiftKey) {
                    if (document.activeElement === firstFocusableElement) {
                        event.preventDefault();
                        lastFocusableElement.focus();
                    }
                } else { // Tab
                    if (document.activeElement === lastFocusableElement) {
                        event.preventDefault();
                        firstFocusableElement.focus();
                    }
                }
            };

            document.addEventListener('keydown', activeFocusTrapHandler);
        }, 100);
    };

    /**
     * Entfernt die aktive "Focus Trap" und stellt den vorherigen Fokus wieder her.
     */
    const removeFocusTrap = () => {
        if (activeFocusTrapHandler) {
            document.removeEventListener('keydown', activeFocusTrapHandler);
            activeFocusTrapHandler = null;
        }
        if (lastFocusedElementBeforePopup) {
            lastFocusedElementBeforePopup.focus();
            lastFocusedElementBeforePopup = null;
        }
    };

    /**
     * Wird aufgerufen, wenn ein Popup geöffnet wird.
     * @param {HTMLElement} popupNode - Das Rootelelement des Popups.
     */
    const handlePopupOpen = (popupNode) => {
        if (activeFocusTrapHandler) return;

        lastFocusedElementBeforePopup = document.activeElement;
        optimizeCloseButton(popupNode);

        const contentContainer = popupNode.querySelector('.jet-popup__container-content');

        if (!contentContainer) {
            console.error('Jet Popup A11y: .jet-popup__container-content nicht gefunden. Fallback auf Timeout.');
            setTimeout(() => setupFocusTrap(popupNode), 500);
            return;
        }

        // Prüfen, ob der Inhalt bereits vorhanden ist (für Popups ohne AJAX-Laden)
        if (contentContainer.children.length > 0) {
            // Mit kurzer Verzögerung ausführen, um dem Browser Zeit zum Rendern zu geben.
            setTimeout(() => setupFocusTrap(popupNode), 50);
            return;
        }

        // Wenn der Container leer ist, auf das Hinzufügen von Inhalt warten.
        const contentObserver = new MutationObserver((mutationsList, observer) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    setTimeout(() => {
                        setupFocusTrap(popupNode);
                    }, 50);

                    observer.disconnect();
                    return;
                }
            }
        });

        contentObserver.observe(contentContainer, { childList: true });
        setTimeout(() => {
            contentObserver.disconnect();
        }, 5000);
    };

    /**
     * Beobachtet DOM-Änderungen, um das Öffnen und Schließen von Popups zu erkennen.
     */
    const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const target = mutation.target;
                if (target.matches('.jet-popup')) {
                    const isShown = target.classList.contains('jet-popup--show-state');
                    const wasShown = mutation.oldValue && mutation.oldValue.includes('jet-popup--show-state');

                    if (isShown && !wasShown) {
                        handlePopupOpen(target);
                    } else if (!isShown && wasShown) {
                        removeFocusTrap();
                    }
                }
            }

            if (mutation.type === 'childList') {
                mutation.removedNodes.forEach(node => {
                    if (node.nodeType === 1 && node.matches('.jet-popup.jet-popup--show-state')) {
                        removeFocusTrap();
                    }
                });
            }
        }
    });

    const config = {
        childList: true,
        subtree: true,
        attributes: true,
        attributeOldValue: true,
        attributeFilter: ['class']
    };

    observer.observe(document.body, config);

    const initialPopup = document.querySelector('.jet-popup.jet-popup--show-state');
    if (initialPopup) {
        handlePopupOpen(initialPopup);
    }
}

export { initPopupCloseButtonOptimization };